import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { AuthContext } from "../App";
import { ArrowLeft, Search, Filter, FileText, Clock, User, Activity } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../utils/dateFormat";

const AuditJournalPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: "",
    action: "",
    entity_id: "",
    limit: 100
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchJournal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchJournal = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      if (filters.action) params.append("action", filters.action);
      if (filters.entity_id) params.append("entity_id", filters.entity_id);
      params.append("limit", filters.limit);
      
      const response = await axios.get(`${API}/admin/audit-journal?${params.toString()}`);
      setEntries(response.data.entries || []);
    } catch (error) {
      toast.error("Failed to fetch audit journal");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJournal();
  };

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE": return "bg-green-500";
      case "MODIFY": return "bg-blue-500";
      case "DELETE": return "bg-red-500";
      case "VERIFY_PAYMENT": return "bg-emerald-500";
      case "FULL_REFUND_CANCEL": return "bg-orange-500";
      default: return "bg-gray-500";
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-sm font-bold hover:underline"
          >
            <ArrowLeft size={16} />
            BACK TO ADMIN
          </button>
        </div>

        <div className="brutal-card mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-10 h-10" strokeWidth={2.5} />
            <div>
              <h1 className="text-4xl font-syne font-bold">AUDIT JOURNAL</h1>
              <p className="text-muted-foreground font-mono">Complete transaction log</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={handleSearch} className="brutal-card mb-8 bg-secondary">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} />
            <h2 className="font-bold">FILTERS</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">Entity Type</label>
              <select
                className="brutal-input w-full"
                value={filters.entity_type}
                onChange={(e) => handleFilterChange("entity_type", e.target.value)}
              >
                <option value="">All Types</option>
                <option value="BOOKING">Karaoke Booking</option>
                <option value="BBQ_BOOKING">BBQ Booking</option>
                <option value="MEMBER">Member</option>
                <option value="PUBLIC_HOLIDAY">Public Holiday</option>
                <option value="BLACKOUT_DATE">Blackout Date</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Action</label>
              <select
                className="brutal-input w-full"
                value={filters.action}
                onChange={(e) => handleFilterChange("action", e.target.value)}
              >
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="MODIFY">Modify</option>
                <option value="DELETE">Delete</option>
                <option value="VERIFY_PAYMENT">Verify Payment</option>
                <option value="BBQ_PAYMENT_VERIFIED">BBQ Payment Verified</option>
                <option value="BBQ_BOOKING_CANCELLED">BBQ Cancelled</option>
                <option value="BBQ_REFUND_VERIFIED">BBQ Refund Verified</option>
                <option value="FULL_REFUND_CANCEL">Full Refund Cancel</option>
                <option value="DATA_WIPE">Data Wipe</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Booking ID</label>
              <input
                type="text"
                className="brutal-input w-full"
                placeholder="e.g., BK0001"
                value={filters.entity_id}
                onChange={(e) => handleFilterChange("entity_id", e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <button type="submit" className="brutal-btn-primary w-full flex items-center justify-center gap-2">
                <Search size={16} />
                SEARCH
              </button>
            </div>
          </div>
        </form>

        {/* Results */}
        <div className="brutal-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">
              <Activity className="inline mr-2" size={20} />
              TRANSACTION LOG ({entries.length} entries)
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="mx-auto mb-4" size={48} />
              <p>No audit entries found</p>
              <p className="text-sm mt-2">Transactions will appear here as they occur</p>
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry, idx) => (
                <div key={idx} className="border-2 border-black p-4 bg-white hover:bg-muted transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 text-xs font-bold text-white ${getActionColor(entry.action)}`}>
                        {entry.action}
                      </span>
                      <span className="px-2 py-1 text-xs font-bold bg-black text-white">
                        {entry.entity_type}
                      </span>
                      <span className="font-mono font-bold text-primary text-lg">
                        {entry.entity_id}
                      </span>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatTimestamp(entry.timestamp)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm mb-3">
                    <User size={14} />
                    <span className="font-bold">{entry.performed_by}</span>
                    <span className="text-muted-foreground">({entry.performed_by_email})</span>
                  </div>

                  {/* Details */}
                  {entry.details && (
                    <div className="bg-muted p-3 rounded text-sm mb-2">
                      <p className="font-bold mb-1">Details:</p>
                      <p className="text-gray-700">{typeof entry.details === 'string' ? entry.details : JSON.stringify(entry.details)}</p>
                    </div>
                  )}

                  {/* Old vs New Values */}
                  {(entry.old_values || entry.new_values) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {entry.old_values && (
                        <div className="bg-red-50 p-3 rounded border border-red-200">
                          <p className="font-bold text-red-600 mb-1">Before:</p>
                          {Object.entries(entry.old_values).slice(0, 5).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-muted-foreground">{key}:</span>
                              <span className="font-mono">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {entry.new_values && (
                        <div className="bg-green-50 p-3 rounded border border-green-200">
                          <p className="font-bold text-green-600 mb-1">After:</p>
                          {Object.entries(entry.new_values).slice(0, 5).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-muted-foreground">{key}:</span>
                              <span className="font-mono">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditJournalPage;
